import { useState, useRef, useEffect, useCallback } from "react";

interface OverrideDialogProps {
  isOpen: boolean;
  mode: "override" | "escalate";
  ruleName: string;
  fieldLabel: string;
  currentValue: string;
  fallbackValue?: string;
  escalationQueue?: string;
  requireReason: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
}

export function OverrideDialog({
  isOpen,
  mode,
  ruleName,
  fieldLabel,
  currentValue,
  fallbackValue,
  escalationQueue,
  requireReason,
  onConfirm,
  onCancel,
}: OverrideDialogProps) {
  const [reason, setReason] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (isOpen) {
      setReason("");
      // Focus the reason input after a tick (dialog render)
      setTimeout(() => inputRef.current?.focus(), 50);
    }
  }, [isOpen]);

  const handleConfirm = useCallback(() => {
    if (requireReason && reason.trim() === "") return;
    onConfirm(reason.trim());
  }, [reason, requireReason, onConfirm]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onCancel();
      } else if (e.key === "Enter" && e.ctrlKey) {
        e.preventDefault();
        handleConfirm();
      }
    },
    [onCancel, handleConfirm],
  );

  if (!isOpen) return null;

  const isOverride = mode === "override";
  const title = isOverride ? "Override Rule" : "Escalate to Supervisor";
  const confirmLabel = isOverride ? "Confirm Override" : "Escalate";
  const accentColor = isOverride ? "#d97706" : "#ea580c";

  return (
    <div className="override-backdrop" onClick={onCancel} onKeyDown={handleKeyDown}>
      <div
        className="override-dialog"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <div className="override-header" style={{ borderBottomColor: accentColor }}>
          {title}
        </div>

        <div className="override-body">
          <div className="override-info">
            <div className="override-info-row">
              <span className="override-label">Rule:</span>
              <span className="override-value">{ruleName}</span>
            </div>
            <div className="override-info-row">
              <span className="override-label">Field:</span>
              <span className="override-value">{fieldLabel}</span>
            </div>
            <div className="override-info-row">
              <span className="override-label">Current value:</span>
              <span className="override-value override-mono">{currentValue || "(empty)"}</span>
            </div>
            {isOverride && fallbackValue !== undefined && (
              <div className="override-info-row">
                <span className="override-label">Will be set to:</span>
                <span className="override-value override-mono override-fallback">
                  {fallbackValue || "(empty)"}
                </span>
              </div>
            )}
            {!isOverride && escalationQueue && (
              <div className="override-info-row">
                <span className="override-label">Target queue:</span>
                <span className="override-value">{escalationQueue}</span>
              </div>
            )}
          </div>

          <div className="override-reason">
            <label className="override-reason-label">
              {isOverride ? "Reason" : "Note for supervisor"}
              {requireReason && <span className="override-required"> (required)</span>}
            </label>
            <textarea
              ref={inputRef}
              className="override-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                isOverride
                  ? "Why is this override acceptable?"
                  : "Context for the supervisor..."
              }
              rows={2}
            />
          </div>
        </div>

        <div className="override-footer">
          <button className="override-btn override-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            className="override-btn override-btn-confirm"
            style={{ backgroundColor: accentColor }}
            onClick={handleConfirm}
            disabled={requireReason && reason.trim() === ""}
          >
            {confirmLabel}
          </button>
        </div>

        <div className="override-hint">
          Ctrl+Enter to confirm &middot; Esc to cancel
        </div>
      </div>

      <style>{`
        .override-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.6);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .override-dialog {
          background: var(--color-bg-secondary, #131313);
          border: 1px solid var(--color-border, #2a2a2a);
          border-radius: 6px;
          width: 420px;
          max-width: 90vw;
        }
        .override-header {
          padding: 10px 16px;
          font-weight: 600;
          font-size: 0.85rem;
          border-bottom: 2px solid;
          color: var(--color-text-primary, #e5e5e5);
        }
        .override-body {
          padding: 12px 16px;
        }
        .override-info {
          display: flex;
          flex-direction: column;
          gap: 4px;
          margin-bottom: 12px;
        }
        .override-info-row {
          display: flex;
          gap: 8px;
          font-size: 0.8rem;
        }
        .override-label {
          color: var(--color-text-muted, #737373);
          flex: 0 0 100px;
        }
        .override-value {
          color: var(--color-text-primary, #e5e5e5);
        }
        .override-mono {
          font-family: var(--font-mono, monospace);
        }
        .override-fallback {
          color: #d97706;
          font-weight: 600;
        }
        .override-reason {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .override-reason-label {
          font-size: 0.75rem;
          color: var(--color-text-secondary, #a3a3a3);
        }
        .override-required {
          color: var(--color-text-muted, #737373);
        }
        .override-reason-input {
          width: 100%;
          padding: 6px 8px;
          font-size: 0.8rem;
          font-family: inherit;
          background: var(--color-bg-primary, #0e0e0e);
          color: var(--color-text-primary, #e5e5e5);
          border: 1px solid var(--color-border, #2a2a2a);
          border-radius: 4px;
          resize: vertical;
          outline: none;
        }
        .override-reason-input:focus {
          border-color: var(--color-primary, #ea580c);
        }
        .override-footer {
          display: flex;
          justify-content: flex-end;
          gap: 8px;
          padding: 10px 16px;
          border-top: 1px solid var(--color-border, #2a2a2a);
        }
        .override-btn {
          padding: 5px 14px;
          font-size: 0.8rem;
          border-radius: 4px;
          border: 1px solid var(--color-border, #2a2a2a);
          cursor: pointer;
          font-weight: 500;
        }
        .override-btn-cancel {
          background: transparent;
          color: var(--color-text-secondary, #a3a3a3);
        }
        .override-btn-confirm {
          color: white;
          border: none;
        }
        .override-btn-confirm:disabled {
          opacity: 0.4;
          cursor: not-allowed;
        }
        .override-hint {
          text-align: center;
          padding: 4px;
          font-size: 0.65rem;
          color: var(--color-text-muted, #525252);
        }
      `}</style>
    </div>
  );
}
